import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../lib/cn'

/**
 * Variant styles for {@link Chip}.
 *
 * All sizing, spacing, and color classes resolve to design tokens from @atlas/tokens
 * (brand). No hardcoded values.
 *
 * @remarks
 * - Extends Badge with removable (X button) functionality
 * - Supports five variants: primary, secondary, danger, success, muted
 * - Supports three sizes: sm, md (default), lg
 *
 * @example
 * ```tsx
 * const classes = chipVariants({ variant: 'primary', size: 'md' })
 * // Returns: "inline-flex items-center gap-1 rounded-ds-full ... bg-ds-primary ..."
 * ```
 */
export const chipVariants = cva(
	// `max-w-full` + `min-w-0` keep a long label inside its container instead of
	// spilling past the pill; the label itself truncates (see the Chip body).
	'inline-flex max-w-full min-w-0 items-center gap-1 font-normal rounded-ds-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring',
	{
		variants: {
			variant: {
				primary: 'bg-ds-primary text-ds-primary-foreground',
				secondary: 'bg-ds-secondary text-ds-text',
				danger: 'bg-ds-danger text-ds-danger-foreground',
				success: 'bg-ds-success text-ds-success-foreground',
				muted: 'bg-ds-surface text-ds-text-muted border border-ds-border',
			},
			// `min-h-*` rather than a fixed `h-*`: a fixed height cannot grow, so a
			// label that wraps rendered its text outside the pill's border. The
			// minimum preserves the original pill proportions for short labels.
			size: {
				sm: 'text-ds-sm px-2 py-0.5 min-h-5',
				md: 'text-ds-base px-3 py-1 min-h-6',
				lg: 'text-ds-lg px-4 py-1.5 min-h-8',
			},
		},
		defaultVariants: { variant: 'primary', size: 'md' },
	},
)

/**
 * Props for the {@link Chip} component.
 *
 * @property variant - Visual style: 'primary' | 'secondary' | 'danger' | 'success' | 'muted'
 * @property size - Chip size: 'sm' | 'md' (default) | 'lg'
 * @property className - Optional additional Tailwind classes
 * @property children - Chip text content
 * @property onRemove - Callback when X button clicked (shows X button when provided)
 * @property removeLabel - Accessible label for the remove button (default "Remove")
 *
 * @example
 * ```tsx
 * <Chip variant="primary" onRemove={() => console.log('Removed')}>
 *   Tag name
 * </Chip>
 * ```
 */
export interface ChipProps extends React.HTMLAttributes<HTMLElement>, VariantProps<typeof chipVariants> {
	/**
	 * Rendered element. `button` for a chip that is a control rather than a label —
	 * a filter toggle, say, where a span would be neither focusable nor operable by
	 * keyboard. `onClick` and `type` are only meaningful with it; a `span` chip that
	 * wants an interaction wants `onRemove`, or `as="button"`.
	 */
	as?: 'span' | 'button'
	/** Button type, for `as="button"`. Defaulted by the caller, never implicitly 'submit'. */
	type?: 'button' | 'submit' | 'reset'
	onRemove?: () => void
	removeLabel?: string
}

/**
 * Chip component for removable tags/labels.
 *
 * @remarks
 * - **Styling:** All colors and sizing use design tokens from `@atlas/tokens`
 *   (brand). No hardcoded values.
 * - **Variants:** Primary (brand primary), secondary, danger, success, muted.
 * - **Sizes:** Small (h-5), medium (h-6, default), large (h-8).
 * - **Shape:** Rounded-full (pill shape) like Badge.
 * - **Removable:** Shows X button when `onRemove` prop provided.
 * - **Usage:** Filter tags, selected items, removable labels.
 *
 * @example Basic usage
 * ```tsx
 * import { Chip } from '@atlas/ui-primitives'
 *
 * function MyComponent() {
 *   return (
 *     <div>
 *       <Chip variant="primary" onRemove={() => console.log('Remove')}>
 *         React
 *       </Chip>
 *       <Chip variant="secondary" onRemove={() => console.log('Remove')}>
 *         TypeScript
 *       </Chip>
 *     </div>
 *   )
 * }
 * ```
 *
 * @example Without remove button
 * ```tsx
 * <Chip variant="success">Non-removable</Chip>
 * ```
 *
 * @see {@link chipVariants} for CVA variant configuration
 * @see packages/ui-primitives/src/chip/chip.stories.tsx for visual examples
 */
export function Chip({
	as: Element = 'span',
	variant,
	size,
	className,
	children,
	onRemove,
	removeLabel,
	...props
}: ChipProps) {
	// A truncated label must stay readable, so expose the full text as a tooltip —
	// but only when it is plain text, and never overriding a caller's own `title`.
	const title = props.title ?? (typeof children === 'string' ? children : undefined)
	// `as` picks the tag at runtime; the cast lands on the tag the caller chose.
	const Tag = Element as 'span'
	return (
		<Tag {...props} title={title} className={cn(chipVariants({ variant, size }), className)}>
			{/* Truncate rather than wrap: a chip is a pill, and a long value code
			    (e.g. 'veterinarian_animal_health_professional') would otherwise break
			    onto a second line and render outside the border. The full text stays
			    reachable via the native tooltip. */}
			<span className="min-w-0 truncate">{children}</span>
			{onRemove && (
				<button
					type="button"
					onClick={onRemove}
					// `shrink-0` so the remove affordance is never squeezed out by a long label.
					className="inline-flex shrink-0 items-center justify-center rounded-ds-full hover:opacity-70 focus:outline-none focus-visible:ring-1 focus-visible:ring-ds-ring"
					aria-label={removeLabel ?? 'Remove'}
				>
					<svg
						width="14"
						height="14"
						viewBox="0 0 14 14"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						aria-hidden="true"
					>
						<line x1="10" y1="4" x2="4" y2="10" />
						<line x1="4" y1="4" x2="10" y2="10" />
					</svg>
				</button>
			)}
		</Tag>
	)
}
